<template>
  <v-dialog
    v-model="showModal"
    max-width="500"
    persistent
    transition="scale-transition"
  >
    <v-card class="unsaved-changes-modal" elevation="8">
      <v-card-title class="modal-header pa-6">
        <v-icon size="32" color="warning" class="mr-3">
          mdi-alert-circle-outline
        </v-icon>
        <span class="text-h5">Unsaved Changes</span>
      </v-card-title>
      
      <v-card-text class="pa-6">
        <div class="modal-content">
          <p class="text-body-1 mb-4">
            You have unsaved changes to 
            <strong>{{ personName || 'the memory' }}</strong>.
          </p>
          <p class="text-body-2 text--secondary mb-4">
            What would you like to do?
          </p>
          
          <v-alert type="warning" variant="tonal" class="mb-4">
            If you leave without saving, your changes will be lost.
          </v-alert>
        </div>
      </v-card-text>
      
      <v-card-actions class="pa-6 pt-0">
        <v-btn
          variant="outlined"
          color="primary"
          @click="handleCancel"
          class="mr-3"
          size="large"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Stay
        </v-btn>
        
        <v-spacer></v-spacer>
        
        <v-btn
          color="warning"
          @click="handleLeave"
          size="large"
          elevation="0"
        >
          <v-icon left>mdi-exit-to-app</v-icon>
          Leave without saving
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  personName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const showModal = ref(props.modelValue)

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  showModal.value = newValue
})

// Watch for modal changes
watch(showModal, (newValue) => {
  emit('update:modelValue', newValue)
})

const handleCancel = () => {
  showModal.value = false
  emit('cancel')
}

const handleLeave = () => {
  showModal.value = false
  emit('confirm')
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.unsaved-changes-modal {
  border-radius: $border-radius-lg;
  overflow: hidden;
  
  .modal-header {
    background: linear-gradient(135deg, rgba($warning, 0.1) 0%, rgba($warning, 0.05) 100%);
    border-bottom: $border-width-thin solid rgba($warning, 0.2);
    
    .v-card-title {
      font-weight: 600;
      color: $text-primary;
    }
  }
  
  .modal-content {
    .text-body-1 {
      color: $text-primary;
      line-height: 1.6;
    }
    
    .text-body-2 {
      color: $text-secondary;
    }
    
    strong {
      color: $primary;
      font-weight: 600;
    }
  }
  
  .v-card-actions {
    .v-btn {
      border-radius: $border-radius;
      font-weight: 500;
      text-transform: none;
      letter-spacing: 0.025em;
      
      &:first-child {
        border-color: $primary;
        color: $primary;
        
        &:hover {
          background: rgba($primary, 0.1);
        }
      }
      
      &:last-child {
        background: $warning;
        color: white !important;
        
        &:hover {
          background: color.adjust($warning, $lightness: -10%);
          color: white !important;
        }
        
        .v-icon {
          color: white !important;
        }
      }
    }
  }
}

// Animation overrides
.v-dialog-transition-enter-active,
.v-dialog-transition-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.v-dialog-transition-enter-from,
.v-dialog-transition-leave-to {
  transform: scale(0.9);
  opacity: 0;
}

@include mobile {
  .unsaved-changes-modal {
    margin: 1rem;
    
    .pa-6 {
      padding: 1.5rem !important;
    }
    
    .v-card-actions {
      flex-direction: column;
      gap: 12px;
      
      .v-btn {
        width: 100%;
        
        &:first-child {
          order: 2;
        }
        
        &:last-child {
          order: 1;
          color: white !important;
          
          .v-icon {
            color: white !important;
          }
        }
      }
      
      .v-spacer {
        display: none;
      }
    }
  }
}
</style>